// 치과 설정 › 환자 정보 처리위탁 (/settings/clinic-delegation) — 2026-10-08 대표 "권고대로"
//   두 부속 확인(데스크 접수 desk-clinic · 환자 앱 등록 pa-clinic)의 상태·확인일을 한 곳에 모으고, 남은 문서 확인·환자 앱 등록 철회를 여기서 한다.
//   근거: docs/법무검토_처리위탁_가입필수동의_2026-10-08.md §3 ☆ A·B·C, P1-2(강제 아님), P2-1(데스크 철회 조항 없음 — 철회 기능 만들지 않음)
//   확인 = 기존 확인 API(acceptDeskClinicConsent·acceptPatientAppClinicConsent, 대표만) 문서별 1건씩.
//   철회 = revokePatientAppClinicConsent(대표만) — 확인 창 문구는 CareAppClinicConsentCard REVOKE_TEXT(정본 L-8) 그대로.
//   직원 = 상태만 + ☆ C 안내.
import React, { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import ClinicDelegationDocBox from '../clinic/ClinicDelegationDocBox';
import { REVOKE_TEXT } from '../clinic/CareAppClinicConsentCard';
import { revokePatientAppClinicConsent, careClinicError } from '../../services/careAppApi';
import {
    useClinicDelegationStatus, pendingItems, acceptClinicDelegationDocs, delegationCheckLabel, delegationFootnote,
    STAFF_TEXT, BOX_MARK_HINT, fmtYmd,
} from '../../utils/clinicDelegation';

function statusOf(it) {
    if (it.err) return { tone: 'err', text: it.err };
    if (it.done) return { tone: 'ok', text: `확인함 (${it.confirmedVersion || it.version}${it.atText ? `, ${it.atText}` : ''})` };
    if (it.revoked) {
        const pending = Number.isFinite(Number(it.purgeAt)) && Number(it.purgeAt) > Date.now();
        return { tone: 'warn', text: pending ? `철회함 — 앱 등록 정보와 제작 기록은 ${fmtYmd(it.purgeAt)}에 삭제됩니다. 그 전에 다시 확인하면 삭제되지 않습니다.` : '철회함' };
    }
    if (it.confirmed && !it.isCurrent) return { tone: 'warn', text: `내용이 바뀌어 다시 확인이 필요합니다 (이전 확인: ${it.confirmedVersion || '-'}${it.atText ? `, ${it.atText}` : ''})` };
    return { tone: 'warn', text: '아직 확인하지 않음' };
}

function ClinicDelegationSettings({ user }) {
    // 훅은 전부 최상단 — early return 위 (Dashboard 사고)
    const businessType = user?.businessType || user?.companyBusinessType;
    const isClinic = businessType === 'clinic' || businessType === 'dental' || businessType === '치과';
    const isStaff = !!user?.companyId || user?.userType === 'staff';
    const { loading, items, reload } = useClinicDelegationStatus(isClinic);
    const [openText, setOpenText] = useState({});
    const [checked, setChecked] = useState(false);
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState([]);
    const [revokeStep, setRevokeStep] = useState(false);
    const [revokeErr, setRevokeErr] = useState('');

    if (!isClinic) {
        return (
            <div style={S.container}>
                <PageHeader icon={ShieldCheck} title="환자 정보 처리위탁" />
                <div style={S.box}><div style={S.sub}>치과 계정에서만 쓰는 화면입니다.</div></div>
            </div>
        );
    }

    const pending = isStaff ? [] : pendingItems(items);
    const pa = items.find(it => it.key === 'pa');
    const canRevoke = !isStaff && pa && pa.canConfirm && pa.confirmed && !pa.revoked;

    const confirm = async () => {
        if (busy || !checked || pending.length === 0) return;
        setBusy(true); setErrors([]);
        try {
            const res = await acceptClinicDelegationDocs(pending);
            setErrors(res.filter(r => !r.ok).map(r => r.error));
            setChecked(false);
            await reload(true);
        } finally { setBusy(false); }
    };

    const revoke = async () => {
        if (busy || !canRevoke) return;
        setBusy(true); setRevokeErr('');
        try {
            const r = await revokePatientAppClinicConsent();
            if (r?.ok !== true) {
                console.error('[환자 정보 처리위탁] 철회 응답 이상: ok=', r?.ok);
                setRevokeErr('철회를 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.');
                return;
            }
            setRevokeStep(false);
            await reload(true);
        } catch (e) {
            setRevokeErr(careClinicError(e, '철회를 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.'));
        } finally { setBusy(false); }
    };

    return (
        <div style={S.container}>
            <PageHeader icon={ShieldCheck} title="환자 정보 처리위탁" subtitle="데스크 접수·환자 앱 등록을 쓸 때 환자 정보의 처리를 덴탈클러스터에 맡기는 확인 문서 2건의 상태" />

            <div style={S.box}>
                {loading && items.length === 0 ? (
                    <div style={S.sub}>불러오는 중…</div>
                ) : items.map(it => {
                    const st = statusOf(it);
                    return (
                        <div key={it.key} style={S.row}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <b style={{ fontSize: 14, color: '#0f172a', flex: 1, minWidth: 200 }}>{`${it.title} — ${it.version}`}</b>
                                <button type="button" onClick={() => setOpenText(o => ({ ...o, [it.key]: !o[it.key] }))} style={S.btnS}>
                                    {openText[it.key] ? '전문 닫기' : '전문 보기'}
                                </button>
                            </div>
                            <div style={st.tone === 'ok' ? S.ok : st.tone === 'err' ? S.err : S.warn}>{st.text}</div>
                            {openText[it.key] && <div style={S.text}>{it.text}</div>}
                            {it.key === 'pa' && canRevoke && !revokeStep && (
                                <div style={{ marginTop: 8 }}>
                                    <button type="button" onClick={() => { setRevokeStep(true); setRevokeErr(''); }} style={S.btnRevoke}>처리위탁 확인 철회</button>
                                </div>
                            )}
                            {it.key === 'pa' && canRevoke && revokeStep && (
                                <div style={S.revokeBox}>
                                    <div>{REVOKE_TEXT}</div>
                                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                        <button type="button" disabled={busy} onClick={revoke} style={{ ...S.btnDanger, opacity: busy ? 0.5 : 1 }}>{busy ? '기록 중…' : '철회'}</button>
                                        <button type="button" disabled={busy} onClick={() => setRevokeStep(false)} style={S.btnS}>취소</button>
                                    </div>
                                </div>
                            )}
                            {it.key === 'pa' && revokeErr && <div style={S.err}>{revokeErr}</div>}
                        </div>
                    );
                })}
                {!loading && items.some(it => !it.done && !it.available && !it.err) && (
                    <div style={S.err}>확인 내용이 최신이 아닌 문서가 있습니다. 화면을 새로고침해 주세요.</div>
                )}
            </div>

            {isStaff ? (
                <div style={S.box}><div style={S.staff}>{STAFF_TEXT}</div></div>
            ) : pending.length > 0 && (
                <div style={S.box}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>확인할 문서</div>
                    {pending.map((it, i) => <ClinicDelegationDocBox key={it.key} no={i + 1} doc={it} />)}
                    <div style={S.hint}>{BOX_MARK_HINT}</div>
                    <label style={{ ...S.agree, ...(checked ? S.agreeOn : null) }}>
                        <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)}
                            style={{ width: 20, height: 20, flexShrink: 0, margin: 0, accentColor: '#0f6b5c' }} />
                        <span style={{ fontWeight: 700 }}>{`${delegationCheckLabel(pending)} (필수)`}</span>
                    </label>
                    <div style={S.hint}>{delegationFootnote(pending.map(it => it.key))}</div>
                    {errors.map((m, i) => <div key={i} style={S.err}>{m}</div>)}
                    <button type="button" disabled={!checked || busy} onClick={confirm} style={{ ...S.btnP, opacity: !checked || busy ? 0.5 : 1 }}>
                        {busy ? '기록 중…' : '확인'}
                    </button>
                </div>
            )}
            <div style={S.note}>데스크 접수 처리위탁 확인은 데스크 접수를 쓰지 않으면 맡겨지는 환자 정보가 없습니다.</div>
        </div>
    );
}

const S = {
    container: { padding: 24, maxWidth: 820, margin: '0 auto' },
    box: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 14 },
    row: { padding: '10px 0', borderBottom: '1px solid #f1f5f9' },
    sub: { fontSize: 13, color: '#64748b' },
    ok: { marginTop: 6, fontSize: 12.5, color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '7px 11px' },
    warn: { marginTop: 6, fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '7px 11px', lineHeight: 1.55 },
    err: { marginTop: 8, fontSize: 12.5, color: '#8a2020', background: '#fdecec', border: '1px solid #f5cfcf', borderRadius: 8, padding: '8px 11px', lineHeight: 1.55 },
    text: { marginTop: 8, fontSize: 12.5, color: '#1e293b', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'keep-all', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', maxHeight: 280, overflowY: 'auto' },
    staff: { fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', lineHeight: 1.6 },
    hint: { marginTop: 10, fontSize: 12, color: '#64748b', lineHeight: 1.5 },
    agree: { display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13.5, color: '#0f172a', marginTop: 6, cursor: 'pointer', background: '#f0fdf9', border: '2px solid #0f6b5c', borderRadius: 10, padding: '11px 14px', lineHeight: 1.55 },
    agreeOn: { background: '#d1fae5' },
    btnP: { marginTop: 12, padding: '9px 18px', background: '#0f6b5c', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
    btnS: { padding: '5px 12px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
    btnRevoke: { padding: '4px 10px', background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    btnDanger: { padding: '6px 14px', background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
    revokeBox: { marginTop: 8, fontSize: 12.5, color: '#7f1d1d', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 12px', lineHeight: 1.6 },
    note: { fontSize: 12, color: '#94a3b8', lineHeight: 1.6, padding: '0 4px' },
};

export default ClinicDelegationSettings;
